/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { CrossIconContainer, StyledDialogTitle, CountText } from "../audit_log_modal/styles";
import ClearIcon from '@mui/icons-material/Clear';
import { capitalize } from 'lodash'
import { IndexTable } from "../index_table";
import { MODAL_TABLE_MAX_HEIGHT } from "../index_table/styles";
import { Loader } from "../loader";
import camelize from "../../utils/camelize";
import { StyledButton } from "../button";
import { useNavigate } from "react-router-dom";
import {
  auditModuleDisplayName,
  auditModuleNarrowDialog,
  auditModuleNavigationPath,
} from "../../constants/auditLogModules";


export const AuditImportDetail = ({ open, setOpen, moduleName, isLoading, details, restaurant }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false)
  }

  // The destination comes from the shared module list; only the extra context each page needs is
  // decided here.
  const handleNavigate = (moduleName) => {
    const path = auditModuleNavigationPath(moduleName)

    if (!path) {
      return
    }

    const state = { restaurant: restaurant }

    if (moduleName === 'order') {
      state.importedOrderIds = details?.map((record) => record?.id).filter(Boolean)
    }

    if (moduleName === 'employee_time_entries') {
      state.date = details?.[0]?.business_date
    }

    navigate(path, { state })
  }

  const tableColumns = (moduleName) => {
    switch(moduleName) {
      case 'employee':
        return [
          "id",
          "first_name",
          "last_name",
        ];
      case 'job':
        return [
          "id",
          "title",
          "pay_basis",
        ]
      case 'order':
        return [
          'id',
          'guid',
          'order_source'
        ]
      case 'employee_time_entries':
        return [
          'id',
          'guid',
          'employee_name'
        ]
      case 'configured_discount':
        return [
          'id',
          'name',
          'guid',
          'active',
        ]
      case 'revenue_center':
      case 'sales_category':
      case 'restaurant_service':
        return [
          'id',
          'name',
          'guid',
        ]
      default:
        return []
    }
  }

  const columns = tableColumns(moduleName)?.map((column) => {
    return {
      key: column,
      label: camelize(column),
    };
  });

  const TableRows = (data) => {
    switch(moduleName){
      case 'employee':
        return data?.map((record) => {
          return {
            id: record?.id,
            first_name: record?.first_name,
            last_name: record?.last_name
          };
        });
      case 'job':
        return data?.map((record) => {
          return {
            id: record?.id,
            title: record?.title,
            pay_basis: capitalize(record?.pay_basis),
          }
        })
      case 'order':
        return data?.map((record) => {
          return {
            id:record?.id,
            guid: record?.order_id,
            order_source: record?.order_source
          }
        })
      case 'employee_time_entries':
        return data?.map((record) => {
          return {
            id: record?.id,
            guid: record?.guid,
            employee_name: record?.employee_name
          }
        })
      case 'configured_discount':
        return data?.map((record) => {
          return {
            id: record?.id,
            name: record?.name,
            guid: record?.guid,
            active: (`${capitalize(record?.active)}`)
          }
        })
      case 'revenue_center':
      case 'sales_category':
      case 'restaurant_service':
        return data?.map((record) => {
          return {
            id: record?.id,
            name: record?.name,
            guid: record?.guid
          }
        })
      default:
        return []
    }
  };

  const rows = TableRows(details);
  const canNavigate = Boolean(auditModuleNavigationPath(moduleName));

  return (
    <React.Fragment>
      <Dialog
        data-testid="audit-import-modal"
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={auditModuleNarrowDialog(moduleName) ? "sm" : "md"}
      >
        <CrossIconContainer>
          <ClearIcon onClick={handleClose} />
        </CrossIconContainer>
        <div>
          <StyledDialogTitle data-testid="audit-import-modal-title">
            {`${auditModuleDisplayName(moduleName)} Details`}
          </StyledDialogTitle>
          <CountText>{`Total count: ${details?.length || 0}`}</CountText>
        </div>

        <DialogContent
          sx={{
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {
            isLoading ? (
              <Loader />
            ) : (
              <IndexTable
                maxHeight={MODAL_TABLE_MAX_HEIGHT}
                columns={columns}
                data={rows || []}
                tableId={`audit-import-${moduleName}`}
                type="entries"
                itemsPerPage={10}
                pageCount={4}
                page={1}
                report={false}
                hideNavigation={true}
              />
            )
          }
        </DialogContent>
        {canNavigate && (
          <DialogActions sx={{justifyContent: 'center', marginBottom: '10px'}}>
            <StyledButton
              data-testid="audit-import-view-details-button"
              onClick={() => handleNavigate(moduleName)}
            >
              View Details
            </StyledButton>
          </DialogActions>
        )}
      </Dialog>
    </React.Fragment>
  )
}
